import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import TabPanel, { a11yProps } from '@/components/tab-panel/tab-panel';
import ManagedResources from '@/screens/attestation/components/managed-resources';
import AttestationOverview from '@/screens/attestation/components/attestation-overview';
import RejectedResources from '@/screens/attestation/components/rejected-resources';
import { ActionCenterV2 } from '@/components/action-center/action-center-v2';
import { ROUTES, urls } from '@/urls';
import { getToken, getUser } from '@/lib/common';
import { AppContainer } from '@/components/ui';
import { StatCard, StatRow, type DsIconName } from '@/components/ds';
import type { Tone } from '@/theme/tokens';
import 'intro.js/introjs.css';
import { Loader } from '@/components/loader/loader';
import { useAttestation } from '@/screens/attestation/components/hooks/useAttestation';
import { useActionItemDetails } from '@/hooks/useActionItemDetails';
import ActionItemTextDialog from '@/components/action-center/action-item-text-dialog';
import { useActionCenterViewController } from '@/hooks/useActionCenterViewController';
import { createAttestationActionCenterHandler } from '@/screens/attestation/action-center-view.config';
import { scrollSectionIntoView } from '@/utils/actionCenterFocus';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';

export interface AttestationTabItem {
  id: string;
  label: string;
  component: React.ReactNode;
  hasAccess: boolean;
  dataIntro: string;
}

function Attestation() {
  const user = getUser();
  const {
    data: attestationData,
    loading: dataLoading,
    error: dataError,
    permissions,
    refetch,
  } = useAttestation();

  const [availableTabs, setAvailableTabs] = useState<AttestationTabItem[]>([]);
  const [value, setValue] = useState(0);
  const [focusFilters, setFocusFilters] = useState<Record<string, string | undefined>>({});

  // Top summary cards (design: attestStats). Derived from the already-fetched
  // attestation data — no new endpoints.
  const attestStats = useMemo<
    Array<{ icon: DsIconName; value: number; label: string; tone: Tone }>
  >(() => {
    const resources = attestationData.resources ?? [];
    // Missing Status is treated as PENDING everywhere in this screen.
    const isPending = (r: any) => (r?.Status ?? 'PENDING') === 'PENDING';

    const pending = resources.filter(isPending).length;
    const confirmed = resources.filter((r: any) => r?.Status === 'CONFIRMED').length;
    const rejected =
      attestationData.rejectedResources?.length ??
      resources.filter((r: any) => r?.Status === 'REJECTED').length;

    const cards: Array<{ icon: DsIconName; value: number; label: string; tone: Tone }> = [
      { icon: 'attestation', value: pending, label: 'Pending attestations', tone: 'warn' },
      { icon: 'shield', value: confirmed, label: 'Confirmed', tone: 'success' },
      { icon: 'warn', value: rejected, label: 'Rejected', tone: 'danger' },
    ];

    // Overdue is only meaningful if resources carry a due-date field (the current
    // API does not). When present, count past-due PENDING resources; otherwise
    // omit the card gracefully rather than show a value we cannot derive.
    const DUE_KEYS = [
      'due_date',
      'dueDate',
      'attestation_due_date',
      'attestationDueDate',
      'next_attestation_date',
      'nextAttestationDate',
    ];
    const dueKey = DUE_KEYS.find((k) => resources.some((r: any) => r?.[k]));
    if (dueKey) {
      const now = Date.now();
      const overdue = resources.filter((r: any) => {
        if (!isPending(r)) return false;
        const t = new Date(r[dueKey]).getTime();
        return Number.isFinite(t) && t < now;
      }).length;
      cards.push({ icon: 'warn', value: overdue, label: 'Overdue', tone: 'warn' });
    }

    return cards;
  }, [attestationData]);

  useEffect(() => {
    if (
      !attestationData.resources.length &&
      !permissions.isResourceOwner &&
      !permissions.isCloudAccountOwner &&
      !permissions.isDepartmentOwner &&
      !permissions.isSystemOwner
    ) {
      return;
    }

    const availableTabs: AttestationTabItem[] = [
      {
        id: 'managed-resources',
        label: 'Managed Resources',
        component: (
          <ManagedResources data={attestationData} onDataChange={refetch} />
        ),
        hasAccess: permissions.isResourceOwner,
        dataIntro:
          'In Managed Resources Section you can view and manage all resources that are under attestation management.',
      },
      {
        id: 'attestation-overview',
        label: 'Attestation Overview',
        component: <AttestationOverview data={attestationData} />,
        hasAccess:
          user?.is_admin ||
          user?.is_sponsor ||
          permissions.isCloudAccountOwner ||
          permissions.isDepartmentOwner ||
          permissions.isSystemOwner,
        dataIntro:
          'Attestation Overview provides a comprehensive view of all attestation activities and their current status.',
      },
      {
        id: 'rejected-resources',
        label: 'Rejected Resources',
        component: (
          <RejectedResources
            data={attestationData}
            filteredRejectedResources={attestationData.rejectedResources}
          />
        ),
        hasAccess: permissions.isCloudAccountOwner,
        dataIntro:
          'Rejected Resources section shows all resources that have been rejected during the attestation process.',
      },
    ];

    setAvailableTabs(availableTabs.filter((tab) => tab.hasAccess));
  }, [attestationData, permissions, user?.id, user?.is_admin, user?.is_sponsor, refetch]);

  const [actionCenterItems, setActionCenterItems] = useState<ActionCenterItemV2[]>([]);
  const [selectedActionItemId, setSelectedActionItemId] = useState<string | null>(null);
  const { item: v2Item } = useActionItemDetails('Attestation', selectedActionItemId, actionCenterItems);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const focusOverviewTab = useCallback(() => {
    const overviewIdx = availableTabs.findIndex((tab) => tab.id === 'attestation-overview');
    if (overviewIdx !== -1) {
      setValue(overviewIdx);
    }
  }, [availableTabs]);

  useActionCenterViewController({
    module: 'Attestation',
    items: actionCenterItems,
    enabled: availableTabs.length > 0,
    handler: createAttestationActionCenterHandler({
      setSelectedItemId: setSelectedActionItemId,
      setIsDialogOpen,
      setFocusFilters,
      focusOverviewTab,
    }),
  });

  // Scroll to attestation filters when action item is clicked (only when item is selected, not when cleared)
  useEffect(() => {
    const isOverviewTab =
      availableTabs.length > 0 && availableTabs[value]?.id === 'attestation-overview';
    if (Object.keys(focusFilters).length > 0 && isOverviewTab && selectedActionItemId && isDialogOpen) {
      scrollSectionIntoView('[data-attestation-filters-section]');
    }
  }, [focusFilters, value, availableTabs, selectedActionItemId, isDialogOpen]);

  // Removed legacy actionCenterDetails fallback fetch; V2 hook is the sole source now

  const handleChange = (_event: any, newValue: any) => {
    setValue(newValue);
  };

  if (!getToken()) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  if (dataLoading) {
    return (
      <AppContainer>
        <Loader />
      </AppContainer>
    );
  }

  if (dataError) {
    return (
      <AppContainer>
        <Alert severity="error">Error: {dataError}</Alert>
      </AppContainer>
    );
  }

  return (
    <AppContainer>

      <ActionCenterV2
        endpointUrl={urls.DevOps.getAttestationActionCenterV2}
        onItemsLoaded={setActionCenterItems}
      />

      <ActionItemTextDialog
        item={v2Item}
        currentUserId={user?.id}
        open={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
          setSelectedActionItemId(null);
        }}
      />

      {attestationData.resources.length > 0 && (
        <StatRow columns={attestStats.length}>
          {attestStats.map((s) => (
            <StatCard
              key={s.label}
              icon={s.icon}
              value={s.value}
              label={s.label}
              tone={s.tone}
            />
          ))}
        </StatRow>
      )}

      <div className="card" style={{ height: '100%', width: '100%' }}>
        <Box sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile>
              {availableTabs.map((tab, index) => (
                <Tab
                  key={tab.id}
                  label={tab.label}
                  {...a11yProps(index)}
                  data-intro={tab.dataIntro}
                />
              ))}
            </Tabs>
          </Box>

          {availableTabs.map((tab, index) => (
            <TabPanel key={tab.id} value={value} index={index}>
              {tab.id === 'attestation-overview'
                ? React.cloneElement(tab.component as React.ReactElement<any>, { ...focusFilters })
                : tab.component}
            </TabPanel>
          ))}
        </Box>
      </div>
    </AppContainer>
  );
}
export default Attestation;
